import { useSelector } from 'react-redux';
import { useFunds } from '../../../../../api/funds';
import { useShareClass } from '../../../../../api/shareclass/hooks/queries';
import {
  createSectionByIdSelector,
  createPageDetailsSelector,
} from '../../../../../redux/pages/selectors';
import { raptorPlatformIdSelector } from '../../../../../redux/raptorPlatform/selectors';
import { createResourceCacheKey } from '../../../../layout/general/SimpleGeneralPageWrapper';
import { useMemo } from 'react';

interface UseFundAndShareClassNameParams {
  clientId: string;
  fundId: string;
  shareClassId?: string;
  pageKey: string; // e.g., 'kid-compare-v2', 'kid-review-v2', 'kid-editor-v2'
  sectionKey?: string; // defaults to 'raidr-v2'
}

interface UseFundAndShareClassNameReturn {
  /** Short fund name / ID from API (e.g. "gemini004") */
  fundName: string;
  /** Full fund name from API (e.g. "London & Capital Global Credit Opportunities Fund") */
  fundNameFull: string;
  shareClassName: string;
  /** ISIN code for the share class (e.g. "IE00B1234567") */
  shareClassIsin: string;
  /** True while fund or share-class name queries are still in flight */
  isLoading: boolean;
  /** True once resolved names are available for API payloads (not UUID fallbacks) */
  areNamesReady: boolean;
  fundsData?: any[];
  shareClassData?: any[];
}

/**
 * Custom hook to fetch and map fund and share class IDs to their display names.
 * This abstracts the common pattern used across multiple KID components.
 */
export const useFundAndShareClassName = ({
  clientId,
  fundId,
  shareClassId,
  pageKey,
  sectionKey = 'raidr-v2',
}: UseFundAndShareClassNameParams): UseFundAndShareClassNameReturn => {
  const sectionDetails = useSelector(createSectionByIdSelector(sectionKey));
  const pageDetails = useSelector(
    createPageDetailsSelector(pageKey, sectionKey),
  );
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const rsResources = createResourceCacheKey(
    sectionDetails?.rsResources,
    pageDetails?.rsResources,
  );

  const fundsQueryEnabled =
    !!sectionDetails?.fundTypes && !!rsResources?.length;

  const { data: fundsData, isPending: isFundsPending } = useFunds(
    {
      fundTypes: sectionDetails?.fundTypes,
      rsResources,
      raptorPlatformId,
    },
    { enabled: fundsQueryEnabled },
  );

  const shareClassQueryEnabled =
    !!clientId && !!fundId && !!shareClassId;

  const { data: shareClassData, isPending: isShareClassPending } =
    useShareClass(
      {
        client_id: clientId,
        fund_id: fundId,
        share_class_id: shareClassId || undefined,
      },
      {
        enabled: shareClassQueryEnabled,
      },
    );

  const matchedFund = useMemo(
    () => fundsData?.find((f) => f.fund_id === fundId),
    [fundsData, fundId],
  );

  const isLoading =
    (fundsQueryEnabled && isFundsPending) ||
    (shareClassQueryEnabled && isShareClassPending);

  const areNamesReady =
    (!fundsQueryEnabled || !isFundsPending) &&
    (!shareClassQueryEnabled || !isShareClassPending) &&
    !!matchedFund?.fund_name &&
    (!shareClassQueryEnabled ||
      !!(
        shareClassData?.[0]?.share_class_name_full ||
        shareClassData?.[0]?.share_class_name
      ));

  // Map IDs to names using API fund_name and fund_name_full.
  // Fall back to route IDs for display only once loading has finished.
  const fundName = useMemo(() => {
    if (matchedFund?.fund_name) return matchedFund.fund_name;
    if (isFundsPending) return '';
    return fundId || 'Fund';
  }, [matchedFund, isFundsPending, fundId]);

  const fundNameFull = useMemo(() => {
    if (matchedFund?.fund_name_full) return matchedFund.fund_name_full;
    if (matchedFund?.fund_name) return matchedFund.fund_name;
    if (isFundsPending) return '';
    return fundId || 'Fund';
  }, [matchedFund, isFundsPending, fundId]);

  const shareClassName = useMemo(() => {
    if (shareClassData?.[0]?.share_class_name_full) {
      return shareClassData[0].share_class_name_full;
    }
    if (shareClassData?.[0]?.share_class_name) {
      return shareClassData[0].share_class_name;
    }
    if (isShareClassPending) return '';
    return shareClassId || 'Share Class';
  }, [shareClassData, isShareClassPending, shareClassId]);

  const shareClassIsin = useMemo(() => {
    if (shareClassData?.[0]?.share_class_isin) {
      return shareClassData[0].share_class_isin;
    }
    if (isShareClassPending) return '';
    return shareClassId || '';
  }, [shareClassData, isShareClassPending, shareClassId]);

  return {
    fundName,
    fundNameFull,
    shareClassName,
    shareClassIsin,
    isLoading,
    areNamesReady,
    fundsData,
    shareClassData,
  };
};
